/**
 * 業務ルールの部品 — 業務ルールの行の組み立て（変更前と変更後の突き合わせ）と、業務ルールのモーダル。
 * 使うのは業務詳細（データの流れの図）で、業務ルールの箱を押すとモーダルを開く。
 *
 * **業務ルールは業務の中の集合で、順番を持たない。** 行は業務ルールの番号の順に並べる。依存から
 * 導いた順に並べると、持たない順番があるように見える。
 *
 * **案件モードは master と案件適用後の 2 回の取得を業務ルールの番号で突き合わせる。** 外した業務
 * ルールも番号の順の位置に残す。中身を見るモーダルは左に変更前、右に変更後を並べる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの行を変更前と変更後の突き合わせつきで番号の順に組み、押した業務ルールの入力と中身をモーダルで出す。
 * @graph-connects RuleSections [calls] 業務ルール 1 件の入力と中身
 * @graph-connects ruleChangeState [calls] 業務ルールの変更前と変更後の突き合わせ
 * @graph-connects DiffSplitPanes [calls] 変更前と変更後の左右比較
 */
import type { BusinessRuleView, GetBusinessRulesOutput } from '@cortex/domain-spec-console';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  DiffAbsentCell,
  DiffCompareToggle,
  DiffSplitPanes,
} from '../components/spec/DiffSplitPanes.js';
import type { SpecDiffState } from '../components/spec/SpecDiffState.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { pjLinkSearch } from './pj-link.js';
import { RULE_ROUTE } from './route-paths.js';
import { RuleSections, ruleChangeState, type SupplierRender } from './rule-detail-sections.js';
import { RuleLink } from './view-links/business-logic.js';
import { pjLinkValue, VIEW_LINK_CLASS } from './view-links/common.js';

/**
 * 一覧の 1 行。案件を選んでいなければ `before` は null で、印は付かない。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの並びの 1 行（業務ルールの変更前と変更後の姿と、案件での扱い）
 * @graph-connects none
 */
export interface RuleListRow {
  /** 業務ルールの番号。 */
  readonly ruleId: number;
  /** 一覧に出す姿（変更後。削除したものだけ変更前）。 */
  readonly shown: BusinessRuleView;
  /** 変更前の姿。案件を選んでいない・案件で足したなら null。 */
  readonly before: BusinessRuleView | null;
  /** 変更後の姿。案件で外したなら null。 */
  readonly after: BusinessRuleView | null;
  /** 案件での扱い。案件を選んでいなければ `unchanged`。 */
  readonly state: SpecDiffState;
}

/**
 * 変更前の業務ロジック。
 *
 * - `null` — 案件を選んでいない
 * - `'missing'` — 案件がこの業務を新しく作った（全業務ルールが追加）
 * - 姿 — master の業務ロジック
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの並びが突き合わせる変更前の姿（案件なし / 案件が新規作成 / master）
 * @graph-connects none
 */
export type RuleListBefore = GetBusinessRulesOutput | 'missing' | null;

/**
 * 一覧の行を作る。業務ルールの番号の順に並べ、案件モードで外した業務ルールも同じ順の位置に置く。
 *
 * @param after 描く業務ロジック（案件を選んでいれば変更後）
 * @param before 変更前の業務ロジック
 * @returns 一覧の行
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの並びの行を、外した業務ルールも含めて業務ルールの番号の順に作る。
 * @graph-connects ruleChangeState [calls] 業務ルールの変更前と変更後の突き合わせ
 */
export function buildRuleListRows(
  after: GetBusinessRulesOutput,
  before: RuleListBefore,
): RuleListRow[] {
  const beforeRules = before === null || before === 'missing' ? [] : before.rules;
  const beforeById = new Map(beforeRules.map((rule) => [rule.ruleId, rule]));
  const afterIds = new Set(after.rules.map((rule) => rule.ruleId));

  const kept = after.rules.map((rule): RuleListRow => {
    const previous = beforeById.get(rule.ruleId) ?? null;
    return {
      ruleId: rule.ruleId,
      shown: rule,
      before: previous,
      after: rule,
      state: before === null ? 'unchanged' : ruleChangeState(previous, rule),
    };
  });
  const removed = beforeRules
    .filter((rule) => !afterIds.has(rule.ruleId))
    .map(
      (rule): RuleListRow => ({
        ruleId: rule.ruleId,
        shown: rule,
        before: rule,
        after: null,
        state: 'deleted',
      }),
    );
  return [...kept, ...removed].sort((left, right) => left.ruleId - right.ruleId);
}

/**
 * 行の案件での扱いを数える。
 *
 * @param rows 一覧の行
 * @returns 追加・変更・削除の件数
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの並びの行から、案件で足した・変えた・外した業務ルールの件数を数える。
 * @graph-connects none
 */
export function countRuleChanges(rows: readonly RuleListRow[]): {
  created: number;
  updated: number;
  deleted: number;
} {
  const count = (state: SpecDiffState) => rows.filter((row) => row.state === state).length;
  return { created: count('created'), updated: count('updated'), deleted: count('deleted') };
}

/** `RuleModal` の props。 @graph-connects none */
interface RuleModalProps {
  /** 開いている行。 */
  row: RuleListRow;
  /** 重ねている案件番号。選んでいなければ undefined。 */
  pjNumber: number | undefined;
  /** 変更前と変更後を左右に並べるか。案件を選んでいないか、この業務ルールに差が無ければ無視する。 */
  split: boolean;
  /** 左右比較の開閉。 */
  onToggleCompare: () => void;
  /** 閉じる。 */
  onClose: () => void;
  /** 一覧に居る業務ルールの番号。入力の出し手がこの中に居れば、そのモーダルへ切り替える。 */
  listedRuleIds: ReadonlySet<number>;
  /** 別の業務ルールのモーダルへ切り替える。 */
  onOpenRule: (ruleId: number) => void;
}

/**
 * 業務ルール 1 件のモーダル。本文は入力と中身の 2 つ、足は業務ルールの画面への入口（と案件モードの
 * 「変更前と比較」）。閉じるのは見出しの ✕ ・ Esc ・ 外を押す（足の「閉じる」は置かない）。案件モードでは
 * 左に変更前・右に変更後、`compare=0` なら変更後 1 面。見出しに案件の札は付けない（押した行の札で読める）。
 *
 * **入力の「←」の出し手が同じ業務の業務ルールなら、そのモーダルへ切り替える**（メインの決定 2026-09-29）。
 * 一覧に居なければ業務ルールの画面へのリンクにする。
 *
 * **案件がこの業務ルールを変えていなければ 1 面で、比較のボタンは押せない。** 業務の中で別の業務ルール
 * が変わっていて一覧が左右比較で開いていても、変わっていない業務ルールを左右に並べると同じ中身が
 * 2 つ出るだけになる。業務ルールの画面（`RulePage`）と同じ判定を行の状態から取る。
 *
 * @param props 開いている行・案件番号・左右比較の開閉・閉じる口・一覧の業務ルールと切り替える口
 * @returns 業務ルールのモーダル
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルール 1 件の入力と中身をモーダルで描き、案件モードでは案件が変えた業務ルールだけ変更前と変更後を左右に並べ、業務ルールの画面への入口を置く。
 * @graph-connects RuleSections [calls] 業務ルール 1 件の入力と中身
 * @graph-connects DiffSplitPanes [calls] 変更前と変更後の左右比較
 */
export function RuleModal({
  row,
  pjNumber,
  split,
  onToggleCompare,
  onClose,
  listedRuleIds,
  onOpenRule,
}: RuleModalProps) {
  const { t } = useTranslation();
  const pjMode = pjNumber !== undefined;
  const faded = row.state === 'unchanged' && pjMode ? 'opacity-60' : '';
  const comparable = row.state !== 'unchanged';
  const shownSplit = split && comparable;

  const renderSupplier: SupplierRender = (supplier) => {
    const name = displayLabel(supplier.label, t, supplier.untranslated);
    if (!listedRuleIds.has(supplier.id)) {
      return (
        <RuleLink ruleId={supplier.id} pjNumber={pjNumber}>
          {name}
        </RuleLink>
      );
    }
    return (
      <button
        type="button"
        className={VIEW_LINK_CLASS}
        data-open-supplier={supplier.id}
        onClick={() => {
          onOpenRule(supplier.id);
        }}
      >
        {name}
      </button>
    );
  };

  const paneOf = (side: 'before' | 'after') => {
    const rule = side === 'before' ? row.before : row.after;
    const counterpart = side === 'before' ? row.after : row.before;
    if (rule === null) {
      return <DiffAbsentCell state={side === 'before' ? 'created' : 'deleted'} />;
    }
    // min-w-0: 列の多い判定表が面の幅を押し広げて反対側へはみ出さないようにする
    return (
      <div className={`min-w-0 ${faded}`}>
        <RuleSections
          rule={rule}
          pjNumber={pjNumber}
          side={side}
          counterpart={counterpart}
          renderSupplier={renderSupplier}
        />
      </div>
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="full"
      data-rule-modal={row.ruleId}
      title={displayLabel(row.shown.label, t, row.shown.untranslated)}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={RULE_ROUTE}
            params={{ ruleId: String(row.ruleId) }}
            {...pjLinkSearch(pjLinkValue(pjNumber))}
            className={`${VIEW_LINK_CLASS} font-bold`}
            data-open-rule={row.ruleId}
          >
            {t('businessLogic.openRule')} →
          </Link>
          <span className="flex-1" />
          {pjMode && (
            <DiffCompareToggle
              split={shownSplit}
              disabled={!comparable}
              onToggle={onToggleCompare}
            />
          )}
        </div>
      }
    >
      {pjMode && shownSplit ? (
        <DiffSplitPanes
          pjNumber={pjNumber === undefined ? null : String(pjNumber)}
          rows={[{ key: String(row.ruleId), before: paneOf('before'), after: paneOf('after') }]}
        />
      ) : (
        paneOf(row.after === null ? 'before' : 'after')
      )}
    </Modal>
  );
}
